import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import LoadingScreen from '../components/LoadingScreen';
import { PATH_DASHBOARD } from '../routes/paths';
import { isAnnotationWorkspaceOnly } from '../utils/annotationWorkspaceAccess';

/**
 * Keep workspace-only accounts within the annotation workspace, including shared
 * batch links. Candidate links become saved-member links before the page mounts;
 * permission and restaurant checks remain with each page and the API.
 */
export default function AnnotationWorkspaceGuard({ children }) {
  const { isAuthenticated, isInitialized, user } = useAuth();
  const { pathname, search, hash } = useLocation();
  const { workspace } = PATH_DASHBOARD.annotations;
  const normalizedPathname = pathname.replace(/\/+$/, '');

  if (!isInitialized) return <LoadingScreen />;

  if (isAuthenticated && isAnnotationWorkspaceOnly(user)) {
    const isWorkspaceAlias = normalizedPathname === '/dashboard/annotations/images-v2';
    if (normalizedPathname !== workspace && !isWorkspaceAlias) return <Navigate to={workspace} replace />;

    const params = new URLSearchParams(search);
    if (params.has('addToBatch')) {
      // Preserve an existing shared batch; never expose candidates outside its members.
      if (!params.has('batch')) params.set('batch', params.get('addToBatch'));
      params.delete('addToBatch');
      return <Navigate to={`${workspace}?${params.toString()}${hash}`} replace />;
    }
    if (isWorkspaceAlias) return <Navigate to={`${workspace}${search}${hash}`} replace />;
  }

  return children;
}

AnnotationWorkspaceGuard.propTypes = {
  children: PropTypes.node,
};
